function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from "react";
import HubspotForm from "react-hubspot-form";
import * as Sentry from "@sentry/nextjs";
import jquery from "jquery";
import { PORTAL_ID } from "client/services/hubspot-service/contact-form-config";
import * as HubspotService from "client/services/hubspot-service/hubspot-service";
import { HUBSPOT_FORM_CALLBACK_TYPE, HUBSPOT_FORM_ERROR_EVENT, HUBSPOT_UNKNOWN_ERROR_MESSAGE, HUBSPOT_UNKNOWN_ERROR_TYPE } from "client/services/hubspot-service/hubspot-constants";
import { withAbTests } from "contexts/ab-tests-context";
import { withGenericConfig } from "contexts/generic-config-context";
import MondayLoaderComponent from "components/core/loader/monday-loader-component";
import BaseForm from "segments/desktop/core-components/base-form/base-form";
import DefaultProps from "./default-props";
import { StyledContactSalesHubspotFormComponent } from "./contact-sales-hubspot-form-component.scss";
import { FIELD_LABEL_MAP } from "./contact-sales-hubspot-form-component-consts";
import { parseHubspotFormData, convertValuesToHubspotFormat } from "../helpers/base-form-parsers-and-converters";
import { handleCompletePostSubmission } from "../helpers/base-form-post-submission";
import { trackFormSubmission, trackHubspotFormSubmission, trackFormReady } from "../helpers/base-form-trackers";
import { HUBSPOT_FORM_SUBMIT_FAILED, HUBSPOT_FORM_VALIDATION_ERROR } from "constants/bigbrain-event-types";
import { trackEvent } from "services/bigbrain-service";
import { getLocaleFormQueryParam } from "utils/url";
const TARGET_NODE_SELECTOR = ".contact-sales-hubspot-form-component";
const ContactSalesHubspotFormComponent = (props)=>{
    const { formId, urlToRedirectAfterSubmission, formMinHeight, withPrivacyNotice, useNewBaseForm, dontRenderPostFormSubmit, abTests, cookies, cloudfrontViewerCountry, translate, localeId, isWebflowFormPage, webflowPageLocaleQueryParam, backgroundColor, textColor, textSize, ctaButtonColor, ctaTextColor, formShadow } = props;
    const [formConfig, setFormConfig] = useState(null);
    const [useBaseForm, setUseBaseForm] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [baseFormRenderError, setBaseFormRenderError] = useState(false);
    const handleHubspotFormReady = (formId, withPrivacyNotice, abTests, cookies, cloudfrontViewerCountry)=>{
        const hiddenData = HubspotService.generateHiddenData({
            withPrivacyNotice,
            abTests,
            cookies,
            cloudfrontViewerCountry
        });
        hiddenData.forEach((data)=>{
            const hiddenField = document.querySelector(`input[name="${data.name}"]`);
            if (hiddenField) {
                hiddenField.value = data.value;
            }
        });
        trackFormReady(formId);
    };
    // Load form configuration
    const loadFormConfig = useCallback(/*#__PURE__*/ _async_to_generator(function*() {
        if (!formId || !PORTAL_ID) {
            setIsLoading(false);
            return;
        }
        // Only load BaseForm config if user enabled it
        if (!useNewBaseForm) {
            setUseBaseForm(false);
            setIsLoading(false);
            return;
        }
        try {
            var _parsedConfig_rows;
            setIsLoading(true);
            const hubspotData = yield HubspotService.fetchHubspotFormData(PORTAL_ID, formId);
            if (!hubspotData) {
                Sentry.captureMessage(`Failed to fetch HubSpot form data with formId: ${formId}`);
                setUseBaseForm(false);
                return;
            }
            const parsedConfig = parseHubspotFormData(hubspotData, localeId, formId);
            if ((parsedConfig === null || parsedConfig === void 0 ? void 0 : (_parsedConfig_rows = parsedConfig.rows) === null || _parsedConfig_rows === void 0 ? void 0 : _parsedConfig_rows.length) > 0) {
                setFormConfig(parsedConfig);
                setUseBaseForm(true);
            } else {
                setUseBaseForm(false);
            }
        } catch (error) {
            Sentry.captureException(error);
            Sentry.captureMessage(`Error fetching HubSpot form data with formId: ${formId}`);
            setUseBaseForm(false);
        } finally{
            setIsLoading(false);
        }
    }), [
        formId,
        localeId,
        useNewBaseForm
    ]);
    useEffect(()=>{
        loadFormConfig();
    }, [
        loadFormConfig
    ]);
    // Setup jQuery for HubSpot form compatibility
    useEffect(()=>{
        // needed for Hubspot form to send submit callback
        window.jQuery = window.jQuery || jquery;
    }, []);
    useEffect(()=>{
        const handleHubspotMessage = (event)=>{
            var _event_data, _event_data1;
            if (((_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.type) === HUBSPOT_FORM_CALLBACK_TYPE && ((_event_data1 = event.data) === null || _event_data1 === void 0 ? void 0 : _event_data1.eventName) === HUBSPOT_FORM_ERROR_EVENT) {
                var _event_data2, _errorData_data;
                const errorData = (_event_data2 = event.data) === null || _event_data2 === void 0 ? void 0 : _event_data2.data;
                const validationResults = (errorData === null || errorData === void 0 ? void 0 : (_errorData_data = errorData.data) === null || _errorData_data === void 0 ? void 0 : _errorData_data.validationResults) || [];
                const errors = validationResults.map((error)=>({
                        errorType: error === null || error === void 0 ? void 0 : error.formSubmissionValidationType,
                        message: error === null || error === void 0 ? void 0 : error.localizedVisitorErrorMessage,
                        fieldName: error === null || error === void 0 ? void 0 : error.fieldName
                    }));
                const firstError = errors[0] || {};
                // Field validation rejections (blocked/invalid email, etc.) are funnel friction, not submit failures.
                // Empty validationResults carry no field error, so they stay genuine failures.
                const eventName = errors.length > 0 ? HUBSPOT_FORM_VALIDATION_ERROR : HUBSPOT_FORM_SUBMIT_FAILED;
                trackEvent(eventName, {
                    kind: formId,
                    info1: firstError.errorType || HUBSPOT_UNKNOWN_ERROR_TYPE,
                    info2: firstError.message || HUBSPOT_UNKNOWN_ERROR_MESSAGE,
                    data: {
                        country: cloudfrontViewerCountry,
                        language: localeId,
                        errors
                    }
                });
            }
        };
        window.addEventListener("message", handleHubspotMessage);
        return ()=>{
            window.removeEventListener("message", handleHubspotMessage);
        };
    }, [
        formId,
        cloudfrontViewerCountry,
        localeId
    ]);
    // Submit form data to HubSpot
    const submitToHubspot = useCallback(function() {
        var _ref = _async_to_generator(function*(convertedFormData) {
            const hiddenData = HubspotService.generateHiddenData({
                withPrivacyNotice,
                abTests,
                cookies,
                cloudfrontViewerCountry
            });
            // Filter out empty hidden fields and combine with form data
            const validHiddenData = hiddenData.filter((field)=>field.value !== null && field.value !== undefined && field.value !== '');
            const allFields = [
                ...convertedFormData,
                ...validHiddenData
            ];
            return yield HubspotService.sendHubspotForm(PORTAL_ID, formId, allFields);
        });
        return function(convertedFormData) {
            return _ref.apply(this, arguments);
        };
    }(), [
        withPrivacyNotice,
        abTests,
        cookies,
        cloudfrontViewerCountry,
        formId
    ]);
    // Main form submission handler
    const handleBaseFormSubmit = useCallback(function() {
        var _ref = _async_to_generator(function*(formData) {
            try {
                const convertedFormData = convertValuesToHubspotFormat(formData);
                // Add hidden fields with default values
                const hiddenFields = (formConfig === null || formConfig === void 0 ? void 0 : formConfig.hiddenFieldsWithDefaults) || [];
                const allFormData = [
                    ...convertedFormData,
                    ...hiddenFields
                ];
                // Track analytics
                trackFormSubmission(allFormData, formId, cloudfrontViewerCountry, localeId);
                // Submit to HubSpot
                const response = yield submitToHubspot(allFormData);
                if (response === null || response === void 0 ? void 0 : response.status) {
                    var _response_errors_, _response_errors;
                    const errorMessage = ((_response_errors = response.errors) === null || _response_errors === void 0 ? void 0 : (_response_errors_ = _response_errors[0]) === null || _response_errors_ === void 0 ? void 0 : _response_errors_.message) || "form.errors.formSubmitError";
                    Sentry.captureMessage(`Error during HubSpot form submission with formId: ${formId}`);
                    return {
                        success: false,
                        errorMessage
                    };
                }
                // Handle successful submission
                return handleCompletePostSubmission(urlToRedirectAfterSubmission, formConfig);
            } catch (error) {
                Sentry.captureException(error);
                return {
                    success: false,
                    errorMessage: "form.errors.formSubmitError"
                };
            }
        });
        return function(formData) {
            return _ref.apply(this, arguments);
        };
    }(), [
        submitToHubspot,
        urlToRedirectAfterSubmission,
        formConfig,
        formId,
        cloudfrontViewerCountry,
        localeId
    ]);
    // Input blur handler for BaseForm
    const handleInputBlur = useCallback((name, value)=>{
        trackFormReady(formId);
    }, [
        formId
    ]);
    // Original HubspotForm handlers (for fallback)
    const handleHubspotFormReadyCallback = useCallback(()=>{
        handleHubspotFormReady(formId, withPrivacyNotice, abTests, cookies, cloudfrontViewerCountry);
    }, [
        withPrivacyNotice,
        abTests,
        cookies,
        cloudfrontViewerCountry,
        formId
    ]);
    const handleHubspotFormSubmit = useCallback((formData)=>{
        // Track form submission
        trackHubspotFormSubmission(formData, formId, cloudfrontViewerCountry, localeId);
        // Handle redirect if specified
        if (urlToRedirectAfterSubmission) {
            notifyParentFrame({
                type: "formSubmit",
                redirectUrl: urlToRedirectAfterSubmission
            });
        }
    }, [
        formId,
        cloudfrontViewerCountry,
        localeId,
        urlToRedirectAfterSubmission
    ]);
    // Handle label translation for fallback HubSpot form
    const translateLabels = useCallback((mutationsList, observer)=>{
        try {
            if (observer) observer.disconnect();
            const labels = document.querySelectorAll(".hs-form label");
            const legalConsent = document.querySelector(".legal-consent-container p");
            const legalConsentLink = legalConsent === null || legalConsent === void 0 ? void 0 : legalConsent.querySelector("a");
            const contactUsButton = document.querySelector(".hs-button.primary.large");
            mutationsList === null || mutationsList === void 0 ? void 0 : mutationsList.forEach((mutation)=>{
                if (mutation.type === "childList") {
                    // Translate form labels
                    labels === null || labels === void 0 ? void 0 : labels.forEach((label)=>{
                        const id = label.id.replace(`-${formId}`, "");
                        const translationKey = FIELD_LABEL_MAP[id];
                        if (translationKey) {
                            label.textContent = translate(translationKey);
                        }
                    });
                    // Translate privacy notice
                    if (legalConsent) {
                        legalConsent.childNodes.forEach((node)=>{
                            if (node.nodeType === Node.TEXT_NODE) {
                                node.nodeValue = translate("forms.privacyNoticeShortenWithoutParam");
                            }
                        });
                        if (legalConsentLink) {
                            legalConsentLink.textContent = translate("forms.privacyPolicy");
                        }
                    }
                    // Translate submit button
                    if (contactUsButton) {
                        contactUsButton.value = translate("contactSales.hubspotForm.form.contactUsButton");
                    }
                }
            });
        } catch (error) {
            Sentry.captureException(error);
        } finally{
            if (observer) {
                const targetNode = document.querySelector(TARGET_NODE_SELECTOR);
                if (targetNode) {
                    const config = {
                        childList: true,
                        subtree: true
                    };
                    observer.observe(targetNode, config);
                }
            }
        }
    }, [
        formId,
        translate,
        isLoading,
        useBaseForm
    ]);
    // Setup translation observer for fallback HubSpot form
    useEffect(()=>{
        if (!localeId && !(isWebflowFormPage && getLocaleFormQueryParam())) {
            return;
        }
        const observer = new MutationObserver((mutationsList)=>translateLabels(mutationsList, observer));
        const targetNode = document.querySelector(TARGET_NODE_SELECTOR);
        if (targetNode) {
            const config = {
                childList: true,
                subtree: true
            };
            observer.observe(targetNode, config);
        }
        return ()=>{
            observer.disconnect();
        };
    }, [
        translateLabels,
        localeId,
        isWebflowFormPage
    ]);
    // Render loading state
    const renderLoadingState = useCallback(()=>/*#__PURE__*/ _jsx("div", {
            className: "monday-loader-wrapper",
            children: /*#__PURE__*/ _jsx(MondayLoaderComponent, {})
        }), []);
    // Render BaseForm component
    const renderBaseForm = useCallback(()=>{
        try {
            return /*#__PURE__*/ _jsx("div", {
                className: "hubspot-contact-form-wrapper",
                children: /*#__PURE__*/ _jsx(BaseForm, {
                    formConfig: formConfig,
                    submitFormAsyncCallback: handleBaseFormSubmit,
                    onInputBlur: handleInputBlur,
                    renderPrivacyNotice: true,
                    translate: translate,
                    localeId: localeId,
                    onEmailChange: ()=>{},
                    dontRenderPostFormSubmit: dontRenderPostFormSubmit
                })
            });
        } catch (error) {
            Sentry.captureException(error);
            setBaseFormRenderError(true);
            return null;
        }
    }, [
        formConfig,
        handleBaseFormSubmit,
        handleInputBlur,
        withPrivacyNotice,
        translate,
        localeId
    ]);
    // Render fallback HubSpot form
    const renderFallbackForm = useCallback(()=>/*#__PURE__*/ _jsx(HubspotForm, {
            portalId: PORTAL_ID,
            formId: formId,
            loading: renderLoadingState(),
            onReady: handleHubspotFormReadyCallback,
            cssClass: "hbspt-form",
            onSubmit: handleHubspotFormSubmit
        }), [
        formId,
        renderLoadingState,
        handleHubspotFormReadyCallback,
        handleHubspotFormSubmit
    ]);
    // Show loading state while determining which form to use
    if (isLoading) {
        return /*#__PURE__*/ _jsx(StyledContactSalesHubspotFormComponent, {
            style: {
                minHeight: formMinHeight
            },
            children: renderLoadingState()
        });
    }
    const shouldRenderBaseForm = useBaseForm && formConfig && !baseFormRenderError;
    return /*#__PURE__*/ _jsx(StyledContactSalesHubspotFormComponent, {
        className: ` ${shouldRenderBaseForm ? 'use-base-form' : 'contact-sales-hubspot-form-component'}`,
        style: {
            minHeight: formMinHeight
        },
        backgroundColor: backgroundColor,
        textColor: textColor,
        textSize: textSize,
        ctaButtonColor: ctaButtonColor,
        ctaTextColor: ctaTextColor,
        formShadow: formShadow,
        children: shouldRenderBaseForm ? renderBaseForm() : renderFallbackForm()
    });
};
export default withAbTests(withGenericConfig(ContactSalesHubspotFormComponent));
ContactSalesHubspotFormComponent.defaultProps = DefaultProps;
